//
//  HelpPanel.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the Help Panel component.
//

import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Divider,
    Group,
    Highlight,
    Menu,
    ScrollArea,
    Stack,
    Text,
    Title,
    Tooltip
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useState, type FC } from "react";
import { Link } from "react-router-dom";

import AppSettings from "src/AppSettings";
import { Permission, type HelpTopic } from "src/api/Tmd.dtos";
import Icon from "src/components/common/Icon";
import RichTextContent from "src/components/common/RichTextContent";
import InlineLoading from "src/components/layouts/loading/InlineLoading";
import LayoutContext from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHasPermission from "src/hooks/authentication/useHasPermission";
import usePageHelp from "src/hooks/data/load/usePageHelp";
import useHelpTopics from "src/hooks/data/load/useHelpTopics";
import type { HelpTourChapter } from "src/contexts/help/HelpContext";
import useHelp from "src/hooks/help/useHelp";

import HelpTopicFormDrawer from "./HelpTopicFormDrawer";

/** Supplies the refresh of the reader's help once the maintained topic is saved. */
type HelpMaintenanceProps = {
    routeKey: string;
    onSaved: () => Promise<unknown>;
};

/**
 * Offers maintainers the editor for the page's topic, including an inactive one.
 *
 * Preconditions: Rendered only for users with HelpTopicsView, which the topic list requires.
 * Postconditions: The action edits the page's existing topic when one is stored, otherwise creates
 * one; it is hidden when the matching create or update permission is missing.
 */
const HelpMaintenance: FC<HelpMaintenanceProps> = ({ routeKey, onSaved }) => {
    const permissions = useCurrentUserPermissions();
    const canCreate = useHasPermission(permissions, Permission.HelpTopicsCreate);
    const canUpdate = useHasPermission(permissions, Permission.HelpTopicsUpdate);
    const [opened, handlers] = useDisclosure(false);
    const topics = useHelpTopics();
    const topic = topics.data?.find(item => item.routeKey === routeKey);

    if (topics.loading || (topic ? !canUpdate : !canCreate)) {
        return null;
    }

    return (
        <>
            <Tooltip label={topic ? "Edit help" : "Add help"}>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={topic ? "Edit help" : "Add help"}
                    onClick={handlers.open}
                >
                    <Icon icon={topic ? AppSettings.icons.edit : AppSettings.icons.add} />
                </ActionIcon>
            </Tooltip>
            <HelpTopicFormDrawer
                id={topic?.id}
                routeKey={routeKey}
                opened={opened}
                onClose={handlers.close}
                onSaved={() => Promise.all([topics.refresh(), onSaved()])}
            />
        </>
    );
};

/** Supplies a page's help and, optionally, words to mark in it. */
type HelpSectionsProps = {
    help: HelpTopic;
    highlight?: readonly string[];
};

/**
 * Renders the help sections the server returned for a page.
 *
 * Postconditions: Highlight terms are marked in section titles and text; without terms the sections
 * render as written.
 */
export const HelpSections: FC<HelpSectionsProps> = ({ help, highlight = [] }) => (
    <Stack gap="lg">
        {help.sections?.map((section, index) => (
            <Box key={section.id ?? index}>
                {section.title && (
                    <Title
                        order={5}
                        mb="xs"
                    >
                        <Highlight
                            component="span"
                            inherit
                            highlight={[...highlight]}
                        >
                            {section.title}
                        </Highlight>
                    </Title>
                )}
                <RichTextContent
                    content={section.content}
                    highlight={highlight}
                />
            </Box>
        ))}
    </Stack>
);

/** Supplies the host's close action; mobile hosts close their drawer when a tour starts. */
export type HelpPanelProps = {
    onClose: () => void;
};

/**
 * Shows the current page's help, the tour action and maintainers' editing action.
 *
 * Postconditions: Help is requested for the route pattern at the layout's trial/institution
 * context, so sections restricted by permission follow the page being viewed. The tour action is
 * offered only when the page has permitted tour steps. All help links to the combined help page;
 * on mobile it also closes the drawer.
 */
const HelpPanel: FC<HelpPanelProps> = ({ onClose }) => {
    const layout = useContext(LayoutContext);
    const permissions = useCurrentUserPermissions();
    const canMaintain = useHasPermission(permissions, Permission.HelpTopicsView);
    const { routeKey, helpRouteKey, desktop, tourSteps, startTour, getTourChapters } = useHelp();
    const [chapters, setChapters] = useState<HelpTourChapter[]>([]);
    const pageHelp = usePageHelp(
        helpRouteKey,
        layout.context.trialId,
        layout.context.institutionId
    );
    const help = pageHelp.data;
    const hasSections = (help?.sections?.length ?? 0) > 0;

    return (
        <Stack
            h="100%"
            gap={0}
        >
            {/*
                The title keeps its full width, so the actions wrap onto their own line, right
                aligned, before the title itself wraps.
            */}
            <Group
                justify="space-between"
                px="sm"
                py="xs"
                gap="xs"
            >
                <Group
                    gap="xs"
                    wrap="nowrap"
                    miw={0}
                >
                    <Icon icon={AppSettings.icons.help} />
                    <Title order={4}>{help?.title || "Help"}</Title>
                </Group>
                <Group
                    gap={4}
                    wrap="nowrap"
                    ml="auto"
                >
                    {routeKey !== "/help" && (
                        <Button
                            component={Link}
                            to="/help"
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            leftSection={<Icon icon={AppSettings.icons.help} />}
                            onClick={desktop ? undefined : onClose}
                        >
                            All help
                        </Button>
                    )}
                    {canMaintain && (
                        <HelpMaintenance
                            routeKey={helpRouteKey}
                            onSaved={pageHelp.refresh}
                        />
                    )}
                    <Tooltip label="Close help">
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label="Close help"
                            onClick={onClose}
                        >
                            <Icon icon={AppSettings.icons.close} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>
            <Divider />
            <ScrollArea
                style={{ flex: 1 }}
                px="sm"
                py="sm"
            >
                <Stack gap="md">
                    {tourSteps.length > 0 && (
                        <Group
                            gap={0}
                            wrap="nowrap"
                        >
                            <Button
                                variant="light"
                                leftSection={<Icon icon={AppSettings.icons.tour} />}
                                onClick={() => startTour()}
                                style={{ flex: 1 }}
                                styles={{
                                    root: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                                }}
                            >
                                Take the page tour
                            </Button>
                            {/* Chapters are listed when the menu opens, from the targets shown then. */}
                            <Menu
                                position="bottom-end"
                                onOpen={() => setChapters(getTourChapters())}
                            >
                                <Menu.Target>
                                    <Button
                                        variant="light"
                                        aria-label="Tour one part of the page"
                                        px="xs"
                                        styles={{
                                            root: {
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                                borderLeft: "1px solid var(--mantine-color-body)"
                                            }
                                        }}
                                    >
                                        <Icon icon={AppSettings.icons.arrowDown} />
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Label>Tour one part</Menu.Label>
                                    {chapters.map(({ chapter, steps }) => (
                                        <Menu.Item
                                            key={chapter}
                                            onClick={() => startTour(chapter)}
                                            rightSection={
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    {steps} {steps === 1 ? "step" : "steps"}
                                                </Text>
                                            }
                                        >
                                            {chapter}
                                        </Menu.Item>
                                    ))}
                                    {chapters.length === 0 && (
                                        <Menu.Item disabled>No parts are shown right now</Menu.Item>
                                    )}
                                </Menu.Dropdown>
                            </Menu>
                        </Group>
                    )}
                    {pageHelp.loading && <InlineLoading height={120} />}
                    {pageHelp.error && (
                        <Alert
                            color="error"
                            title="Help could not be loaded"
                        >
                            {pageHelp.error.message}
                        </Alert>
                    )}
                    {help && hasSections && <HelpSections help={help} />}
                    {!pageHelp.loading && !pageHelp.error && !hasSections && (
                        <Text
                            c="dimmed"
                            size="sm"
                        >
                            There is no help for this page yet.
                        </Text>
                    )}
                </Stack>
            </ScrollArea>
        </Stack>
    );
};

export default HelpPanel;
